import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText, Search, Download, Printer, Eye, Settings, Save,
  Receipt, DollarSign, Calendar, Hash, ChevronLeft, ChevronRight, X,
  Building2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';

// ===== INVOICE DETAIL MODAL =====
function InvoiceModal({ invoice, onClose, t, lang }) {
  if (!invoice) return null;
  const date = new Date(invoice.created_at);
  const handlePrint = () => window.print();
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="invoice-detail-modal">
        {/* Invoice Content */}
        <div className="p-8 text-black" dir="rtl" id="invoice-print-area">
          {/* Header */}
          <div className="text-center border-b-2 border-gray-800 pb-4 mb-4">
            <p className="text-xs text-gray-500 mb-1">{t('invoice.simplifiedTaxInvoice')}</p>
            <h1 className="text-xl font-bold">{invoice.business_name_ar || invoice.branch_name}</h1>
            {invoice.business_name_en && <p className="text-sm text-gray-600">{invoice.business_name_en}</p>}
            {invoice.business_address && <p className="text-xs text-gray-500 mt-1">{invoice.business_address}{invoice.business_city ? ` - ${invoice.business_city}` : ''}</p>}
            {invoice.branch_phone && <p className="text-xs text-gray-500">{invoice.branch_phone}</p>}
          </div>

          {/* Tax Info */}
          <div className="grid grid-cols-2 gap-3 text-xs mb-4">
            {invoice.vat_number && (
              <div className="bg-gray-50 p-2 rounded"><span className="text-gray-500">{t('invoice.vatNumber')}:</span> <span className="font-mono font-bold">{invoice.vat_number}</span></div>
            )}
            {invoice.cr_number && (
              <div className="bg-gray-50 p-2 rounded"><span className="text-gray-500">{t('invoice.crNumber')}:</span> <span className="font-mono font-bold">{invoice.cr_number}</span></div>
            )}
            <div className="bg-gray-50 p-2 rounded"><span className="text-gray-500">{t('invoice.invoiceNumber')}:</span> <span className="font-mono font-bold">{invoice.invoice_number}</span></div>
            <div className="bg-gray-50 p-2 rounded"><span className="text-gray-500">{t('invoice.invoiceDate')}:</span> <span>{date.toLocaleDateString('ar-SA')} {date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}</span></div>
          </div>

          {/* Customer */}
          {invoice.customer_name && (
            <div className="mb-4 text-sm">
              <span className="text-gray-500">{t('invoice.buyer')}:</span> <span className="font-medium">{invoice.customer_name}</span>
              {invoice.customer_phone && <span className="text-gray-500 ms-3">{invoice.customer_phone}</span>}
            </div>
          )}

          {/* Items Table */}
          <table className="w-full text-sm mb-4">
            <thead>
              <tr className="border-y-2 border-gray-800">
                <th className="text-right py-2 font-bold">{t('invoice.itemDescription')}</th>
                <th className="text-center py-2 font-bold w-16">{t('invoice.quantity')}</th>
                <th className="text-center py-2 font-bold w-24">{t('invoice.unitPrice')}</th>
                <th className="text-left py-2 font-bold w-24">{t('invoice.lineTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items?.map((item, i) => (
                <tr key={i} className="border-b border-gray-200">
                  <td className="py-2">{item.name_ar || item.name}</td>
                  <td className="text-center py-2">{item.quantity}</td>
                  <td className="text-center py-2">{item.price?.toFixed(2)}</td>
                  <td className="text-left py-2">{item.line_total?.toFixed(2) || (item.price * item.quantity).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div className="border-t-2 border-gray-800 pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span>{t('invoice.subtotal')}</span><span>{invoice.subtotal?.toFixed(2)} {t('common.sar')}</span></div>
            {invoice.discount_amount > 0 && (
              <div className="flex justify-between text-red-600"><span>{t('invoice.discount')}</span><span>-{invoice.discount_amount?.toFixed(2)} {t('common.sar')}</span></div>
            )}
            <div className="flex justify-between"><span>{t('invoice.taxableAmount')}</span><span>{(invoice.subtotal - (invoice.discount_amount || 0)).toFixed(2)} {t('common.sar')}</span></div>
            <div className="flex justify-between"><span>{t('invoice.vatAmount')} (15%)</span><span>{invoice.vat_amount?.toFixed(2)} {t('common.sar')}</span></div>
            <div className="flex justify-between font-bold text-lg border-t-2 border-gray-800 pt-2">
              <span>{t('invoice.totalDue')}</span><span>{invoice.total?.toFixed(2)} {t('common.sar')}</span>
            </div>
            <div className="text-xs text-gray-500">
              {t('invoice.paymentMethod')}: {invoice.payment_method === 'cash' ? t('pos.cash') : invoice.payment_method === 'card' ? t('pos.card') : t('pos.transfer')}
            </div>
          </div>

          {/* ZATCA QR */}
          {invoice.zatca_qr && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
              <div>
                <QRCode value={invoice.zatca_qr} size={100} />
              </div>
              <div className="text-left">
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">{t('invoice.zatcaCompliant')}</Badge>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t flex gap-2 bg-gray-50 rounded-b-2xl">
          <Button variant="outline" onClick={handlePrint} className="flex-1 gap-2" data-testid="invoice-print-btn">
            <Printer className="w-4 h-4" />{t('invoice.print')}
          </Button>
          <Button variant="outline" onClick={onClose} className="flex-1">{t('common.close')}</Button>
        </div>
      </div>
    </div>
  );
}

// ===== BUSINESS SETTINGS TAB =====
function BusinessSettingsTab({ branchId, API, authHeaders, t }) {
  const [form, setForm] = useState({
    business_name_ar: '', business_name_en: '', vat_number: '', cr_number: '', address: '', city: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (branchId) fetchSettings();
  }, [branchId]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/business-settings?branch_id=${branchId}`, authHeaders());
      if (res.data?.vat_number) setForm(res.data);
    } catch { /* empty */ }
  };

  const save = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/business-settings`, { ...form, branch_id: branchId }, authHeaders());
      toast.success(t('invoice.settingsSaved'));
    } catch (e) { toast.error(e.response?.data?.detail || t('common.failed')); }
    finally { setLoading(false); }
  };

  return (
    <Card className="max-w-2xl" data-testid="business-settings-form">
      <CardContent className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-foreground">{t('invoice.businessNameAr')}</label>
            <Input value={form.business_name_ar} onChange={e => setForm({ ...form, business_name_ar: e.target.value })} className="mt-1" dir="rtl" data-testid="biz-name-ar" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">{t('invoice.businessNameEn')}</label>
            <Input value={form.business_name_en} onChange={e => setForm({ ...form, business_name_en: e.target.value })} className="mt-1" dir="ltr" data-testid="biz-name-en" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">{t('invoice.vatNumber')}</label>
            <Input value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value })} className="mt-1 font-mono" dir="ltr" placeholder="300000000000003" data-testid="biz-vat" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">{t('invoice.crNumber')}</label>
            <Input value={form.cr_number} onChange={e => setForm({ ...form, cr_number: e.target.value })} className="mt-1 font-mono" dir="ltr" placeholder="1010000000" data-testid="biz-cr" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">{t('invoice.address')}</label>
            <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="mt-1" data-testid="biz-address" />
          </div>
          <div>
            <label className="text-sm font-medium text-foreground">{t('invoice.city')}</label>
            <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="mt-1" data-testid="biz-city" />
          </div>
        </div>
        <Button onClick={save} disabled={loading} className="gap-2 bg-gradient-to-r from-amber-500 to-orange-600" data-testid="biz-save-btn">
          <Save className="w-4 h-4" />{t('invoice.saveSettings')}
        </Button>
      </CardContent>
    </Card>
  );
}

// ===== MAIN INVOICES PAGE =====
export default function InvoicesPage() {
  const { authHeaders, API, activeBranch } = useAuth();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const branchId = activeBranch?.id;

  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    if (branchId) { fetchInvoices(); fetchStats(); }
  }, [branchId, page, search]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ branch_id: branchId, page: String(page), limit: '15' });
      if (search) params.set('search', search);
      const res = await axios.get(`${API}/invoices?${params}`, authHeaders());
      setInvoices(res.data.invoices || []);
      setTotalPages(res.data.pages || 1);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/invoices/stats/summary?branch_id=${branchId}`, authHeaders());
      setStats(res.data);
    } catch { /* empty */ }
  };

  const viewInvoice = async (id) => {
    try {
      const res = await axios.get(`${API}/invoices/${id}`, authHeaders());
      setSelectedInvoice(res.data);
    } catch { toast.error(t('common.failed')); }
  };

  const paymentLabel = (m) => m === 'cash' ? t('pos.cash') : m === 'card' ? t('pos.card') : t('pos.transfer');

  return (
    <ModuleLayout moduleKey="finance">
      <div className="space-y-6" data-testid="invoices-page">
        {/* Header */}
        <div>
          <h1 className="font-bold text-2xl text-foreground flex items-center gap-2" style={{ fontFamily: 'Tajawal, sans-serif' }}>
            <FileText className="w-6 h-6 text-primary" /> {t('invoice.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('invoice.subtitle')}</p>
        </div>

        <Tabs defaultValue="invoices">
          <TabsList>
            <TabsTrigger value="invoices" data-testid="tab-invoices">{t('invoice.invoices')}</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">{t('invoice.businessSettings')}</TabsTrigger>
          </TabsList>

          <TabsContent value="invoices" className="space-y-4 mt-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: t('invoice.totalInvoices'), value: stats.total_invoices || 0, icon: Hash, color: 'text-blue-500' },
                { label: t('invoice.todayInvoices'), value: stats.today_invoices || 0, icon: Calendar, color: 'text-amber-500' },
                { label: t('invoice.totalRevenue'), value: `${(stats.total_revenue || 0).toFixed(0)} ${t('common.sar')}`, icon: DollarSign, color: 'text-emerald-500' },
                { label: t('invoice.totalVat'), value: `${(stats.total_vat || 0).toFixed(0)} ${t('common.sar')}`, icon: Receipt, color: 'text-purple-500' },
              ].map((s, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <s.icon className={`w-5 h-5 ${s.color}`} />
                    </div>
                    <p className="text-2xl font-bold">{s.value}</p>
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-muted-foreground" />
              <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder={t('invoice.searchPlaceholder')} className="ps-10" data-testid="invoice-search" />
            </div>

            {/* Invoices Table */}
            <Card>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-start p-3 font-medium">{t('invoice.invoiceNumber')}</th>
                      <th className="text-start p-3 font-medium">{t('invoice.invoiceDate')}</th>
                      <th className="text-start p-3 font-medium">{t('pos.customerName')}</th>
                      <th className="text-start p-3 font-medium">{t('invoice.subtotal')}</th>
                      <th className="text-start p-3 font-medium">{t('invoice.vatAmount')}</th>
                      <th className="text-start p-3 font-medium">{t('invoice.totalDue')}</th>
                      <th className="text-start p-3 font-medium">{t('invoice.paymentMethod')}</th>
                      <th className="text-center p-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">{t('common.loading')}</td></tr>
                    ) : invoices.length === 0 ? (
                      <tr><td colSpan={8} className="text-center py-10">
                        <FileText className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                        <p className="text-muted-foreground text-sm">{t('invoice.noInvoices')}</p>
                        <p className="text-muted-foreground text-xs">{t('invoice.noInvoicesDesc')}</p>
                      </td></tr>
                    ) : invoices.map(inv => (
                      <tr key={inv.id} className="border-b hover:bg-muted/30 transition-colors" data-testid={`invoice-row-${inv.id}`}>
                        <td className="p-3 font-mono font-medium text-amber-600">{inv.invoice_number}</td>
                        <td className="p-3 text-muted-foreground text-xs">{new Date(inv.created_at).toLocaleDateString(lang === 'ar' ? 'ar-SA' : 'en-US')} {new Date(inv.created_at).toLocaleTimeString(lang === 'ar' ? 'ar-SA' : 'en-US', { hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="p-3">{inv.customer_name || `POS #${inv.order_number}`}</td>
                        <td className="p-3">{inv.subtotal?.toFixed(2)}</td>
                        <td className="p-3">{inv.vat_amount?.toFixed(2)}</td>
                        <td className="p-3 font-bold">{inv.total?.toFixed(2)} {t('common.sar')}</td>
                        <td className="p-3"><Badge variant="outline" className="text-xs">{paymentLabel(inv.payment_method)}</Badge></td>
                        <td className="p-3 text-center">
                          <Button variant="ghost" size="sm" onClick={() => viewInvoice(inv.id)} className="h-7 gap-1 text-xs" data-testid={`view-invoice-${inv.id}`}>
                            <Eye className="w-3.5 h-3.5" />{t('invoice.viewInvoice')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 p-3 border-t">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <BusinessSettingsTab branchId={branchId} API={API} authHeaders={authHeaders} t={t} />
          </TabsContent>
        </Tabs>
      </div>

      {selectedInvoice && <InvoiceModal invoice={selectedInvoice} onClose={() => setSelectedInvoice(null)} t={t} lang={lang} />}
    </ModuleLayout>
  );
}
